import { ruPlural } from '@/lib/content/declension';
import { ruCountryFor } from '@/i18n/countryCases';

/**
 * UI-тексты общих сервис-виджетов:
 *  - GfServiceLedger (сравнительный реестр тарифов/маршрутов),
 *  - GfServiceFaqPage (страница FAQ сервиса),
 *  - GfCompareLedger (реестр городов на /cities).
 * Только инлайн-литералы этих компонентов; общие надзаголовки/якоря/теги — в servicePage.
 * Интерполируемые строки — функции. Единицы времени (ч/мин) — текстовые подписи.
 * Валютные символы и форматирование чисел остаются в компонентах (чистая логика).
 */
export const serviceWidgetsUi = {
    en: {
        // ── GfServiceLedger ──
        ledger: {
            rec: 'Best',
            select: 'Select →',
            scopeType: 'Type',
            filterAria: 'Filter',
            filtersAria: 'Filters',
            moreFilters: 'More filters',
            of: 'of',
            resetFilters: 'Reset filters',
            showMore: (n) => `Show ${n} more`,
            // Единицы измерения в ячейках
            hourShort: 'h',
            minShort: 'min',
        },
        // ── GfServiceFaqPage ──
        faqPage: {
            // Метки известных категорий FAQ (fallback — гуманизация ключа)
            cat: {
                basics: 'Basics',
                setup: 'Setup',
                payment: 'Payment & plans',
                coverage: 'Coverage',
                trouble: 'Troubleshooting',
                general: 'General',
                device: 'Devices',
            },
            transportLabel: 'Transport',
            catFallback: 'Questions',
            plansFor: (service) => `${service} plans`,
            deviceCheck: 'Device check',
            promoDeals: 'Promo codes & deals',
            findPlan: 'Find a plan in a minute',
            back: (service, name) => `← ${service}${name ? ' for ' + name : ''}`,
            titleFallback: (service) => `${service} questions & answers`,
            categoriesAria: 'Question categories',
            seeAlso: 'See also',
            ctaTitle: 'Ready to connect?',
            ctaText: 'Pick a plan and get online the moment you land.',
            ctaBtn: 'Choose a plan →',
            // Транспортный FAQ звал «выбрать тариф eSIM» (обход 01.10.2026) — CTA по вертикали.
            ctaBy: {
                vpn: { title: 'Ready to pick a VPN?', text: 'Compare providers that work in the country and set one up before you fly.', btn: 'Compare VPNs →' },
                transport: { title: 'Need a car or a transfer?', text: 'Compare car rental and transfers across the country — book directly with the provider.', btn: 'Car rental & transfers →' },
                tours: { title: 'Ready to pick a tour?', text: 'Popular tours with prices and reviews — book directly with the operator.', btn: 'Tours and prices →' },
            },
            hubLabel: { vpn: 'VPN comparison', transport: 'Car rental & transfers', tours: 'Tours and prices' },
            toursLabel: 'Tours',
            airportLink: (name, iata) => `Transfer from ${name} airport (${iata.toUpperCase()})`,
        },
        // ── GfCompareLedger ──
        compare: {
            all: 'All regions',
            city: 'City',
            region: 'Region',
            when: 'When to go',
            guide: 'Guide →',
            regionAria: 'Region',
            allRegions: (name) => `All regions${name ? ' · ' + name : ''}`,
            cityCount: (n) => `${n} ${n === 1 ? 'city' : 'cities'}`,
        },
    },
    ru: {
        // ── GfServiceLedger ──
        ledger: {
            rec: 'Выгодно',
            select: 'Выбрать →',
            scopeType: 'Тип',
            filterAria: 'Фильтр',
            filtersAria: 'Фильтры',
            moreFilters: 'Ещё фильтры',
            of: 'из',
            resetFilters: 'Сбросить фильтры',
            showMore: (n) => `Показать ещё ${n}`,
            hourShort: 'ч',
            minShort: 'мин',
        },
        // ── GfServiceFaqPage ──
        faqPage: {
            cat: {
                basics: 'Основное',
                setup: 'Подключение',
                payment: 'Оплата и тарифы',
                coverage: 'Связь и покрытие',
                trouble: 'Проблемы',
                general: 'Общее',
                device: 'Устройства',
            },
            transportLabel: 'Транспорт',
            catFallback: 'Вопросы',
            plansFor: (service) => `Тарифы ${service}`,
            deviceCheck: 'Проверка устройства',
            promoDeals: 'Промокоды и скидки',
            findPlan: 'Подобрать тариф за минуту',
            back: (service, name) => `← ${service}${name ? ' ' + ruCountryFor(name) : ''}`,
            titleFallback: (service) => `Вопросы и ответы про ${service}`,
            categoriesAria: 'Категории вопросов',
            seeAlso: 'Смотрите также',
            ctaTitle: 'Готовы подключиться?',
            ctaText: 'Выберите тариф и пользуйтесь интернетом сразу по прилёту.',
            ctaBtn: 'Выбрать тариф →',
            ctaBy: {
                vpn: { title: 'Выбираете VPN?', text: 'Сравните сервисы, которые работают в стране, и настройте до вылета.', btn: 'Сравнить VPN →' },
                transport: { title: 'Нужна машина или трансфер?', text: 'Сравните прокат авто и трансферы по стране — бронирование напрямую у провайдера.', btn: 'Аренда авто и трансферы →' },
                tours: { title: 'Выбираете экскурсию?', text: 'Популярные туры с ценами и отзывами — бронирование напрямую у организатора.', btn: 'Туры и цены →' },
            },
            hubLabel: { vpn: 'Сравнение VPN', transport: 'Аренда авто и трансферы', tours: 'Туры и цены' },
            toursLabel: 'Туры',
            airportLink: (name, iata) => `Трансфер из аэропорта ${name} (${iata.toUpperCase()})`,
        },
        // ── GfCompareLedger ──
        compare: {
            all: 'Все регионы',
            city: 'Город',
            region: 'Регион',
            when: 'Когда ехать',
            guide: 'Гайд →',
            regionAria: 'Регион',
            allRegions: (name) => `Все регионы${name ? ' · ' + name : ''}`,
            cityCount: (n) => `${n} ${ruPlural(n, ['город', 'города', 'городов'])}`,
        },
    },
};
